/**
 * Theme mapping utilities for runtime theming
 * Converts COLORS constants to CSS custom properties
 */

import { COLORS } from './colors';

export const THEME_NAMES = ['light', 'dark', 'custom'] as const;
export type ThemeName = (typeof THEME_NAMES)[number];

/**
 * Generates CSS custom properties from the COLORS object
 * Example output: { '--color-brand-500': '#3b82f6', ... }
 */
export function generateCSSVariables(): Record<string, string> {
  const cssVars: Record<string, string> = {};

  Object.entries(COLORS).forEach(([palette, shades]) => {
    Object.entries(shades).forEach(([shade, value]) => {
      cssVars[`--color-${palette}-${shade}`] = value;
    });
  });

  return cssVars;
}

/**
 * Light theme color mappings
 * Maps semantic colors to specific palette/shade combinations
 * Change colors in colors.ts and they'll automatically update here
 */
export const lightTheme = {
  // Background colors
  '--bg-primary': '#ffffff',
  '--bg-secondary': COLORS.neutral[100],
  '--bg-tertiary': COLORS.neutral[200],
  '--bg-card': '#ffffff',
  '--bg-elevated': COLORS.neutral[50],

  // Text colors
  '--text-primary': COLORS.neutral[900],
  '--text-secondary': COLORS.neutral[700],
  '--text-tertiary': COLORS.neutral[500],
  '--text-muted': COLORS.neutral[400],
  '--text-inverse': '#ffffff',

  // Border colors
  '--border-primary': COLORS.neutral[300],
  '--border-secondary': COLORS.neutral[200],
  '--border-light': COLORS.neutral[100],

  // Interactive/Button colors
  '--interactive-primary': COLORS.brand[600],
  '--interactive-primary-hover': COLORS.brand[700],
  '--interactive-primary-active': COLORS.brand[800],
  '--interactive-secondary': COLORS.neutral[100],
  '--interactive-secondary-hover': COLORS.neutral[200],

  // Brand colors (for easy access)
  '--brand-primary': COLORS.brand[600],
  '--brand-light': COLORS.brand[100],
  '--brand-dark': COLORS.brand[800],

  // Status colors
  '--status-success': COLORS.success[600],
  '--status-success-light': COLORS.success[100],
  '--status-danger': COLORS.danger[600],
  '--status-danger-light': COLORS.danger[100],
  '--status-warn': COLORS.warn[600],
  '--status-warn-light': COLORS.warn[100],
  '--status-info': COLORS.info[600],
  '--status-info-light': COLORS.info[100],

  // Surface colors
  '--surface-overlay': 'rgba(0, 0, 0, 0.5)',
  '--surface-hover': COLORS.neutral[50],
} as const;

/**
 * Dark theme color mappings
 * Maps semantic colors to specific palette/shade combinations
 * Change colors in colors.ts and they'll automatically update here
 */
export const darkTheme = {
  // Background colors
  '--bg-primary': COLORS.neutral[900],
  '--bg-secondary': COLORS.neutral[800],
  '--bg-tertiary': COLORS.neutral[700],
  '--bg-card': COLORS.neutral[800],
  '--bg-elevated': COLORS.neutral[800],

  // Text colors
  '--text-primary': COLORS.neutral[50],
  '--text-secondary': COLORS.neutral[300],
  '--text-tertiary': COLORS.neutral[400],
  '--text-muted': COLORS.neutral[500],
  '--text-inverse': COLORS.neutral[900],

  // Border colors
  '--border-primary': COLORS.neutral[700],
  '--border-secondary': COLORS.neutral[800],
  '--border-light': COLORS.neutral[700],

  // Interactive/Button colors
  '--interactive-primary': COLORS.brand[500],
  '--interactive-primary-hover': COLORS.brand[400],
  '--interactive-primary-active': COLORS.brand[300],
  '--interactive-secondary': COLORS.neutral[800],
  '--interactive-secondary-hover': COLORS.neutral[700],

  // Brand colors (for easy access)
  '--brand-primary': COLORS.brand[500],
  '--brand-light': COLORS.brand[400],
  '--brand-dark': COLORS.brand[700],

  // Status colors
  '--status-success': COLORS.success[500],
  '--status-success-light': COLORS.success[900],
  '--status-danger': COLORS.danger[500],
  '--status-danger-light': COLORS.danger[900],
  '--status-warn': COLORS.warn[500],
  '--status-warn-light': COLORS.warn[900],
  '--status-info': COLORS.info[500],
  '--status-info-light': COLORS.info[900],

  // Surface colors
  '--surface-overlay': 'rgba(0, 0, 0, 0.8)',
  '--surface-hover': COLORS.neutral[800],
} as const;

/**
 * Applies theme CSS variables to the document root
 */
export function applyTheme(theme: typeof lightTheme | typeof darkTheme): void {
  const root = document.documentElement;
  const allVars = { ...generateCSSVariables(), ...theme };

  Object.entries(allVars).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
}
